import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Lightbox from 'react-image-lightbox';
import { BsPaperclip } from 'react-icons/bs';
import { useDropzone } from 'react-dropzone';
import { MdCloudUpload } from 'react-icons/md';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import i18n from 'i18n-js';
import { useConfirm } from 'hooks';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';

const imagePattern =
  /(?:(?:https?:\/\/))[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b(?:[-a-zA-Z0-9@:%_+.~#?&/=]*(\.jpg|\.png|\.jpeg))/g;

const ligthBoxStyles = {
  overlay: {
    zIndex: '1300',
  },
};

const fileInputBaseSx = {
  boxSizing: 'border-box',
  borderRadius: '4px',
  backgroundColor: '#F2F2F2',
  borderWidth: 2,
  borderColor: '#A8A8A8',
  borderStyle: 'dashed',
  padding: '30px 20px',
  transition: 'border .24s ease-in-out',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  outline: 'none',
  cursor: 'pointer',
};

const fileInputCaptionSx = {
  userSelect: 'none',
  color: 'rgba(0, 0, 0, 0.54)',
};

const fileInputCaptionActionSx = {
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: 4,
};

const fileListSx = {
  margin: 0,
  padding: 0,
  marginTop: 1.5,
  maxWidth: '100%',
};

const fileItemSx = {
  display: 'flex',
};

const fileNameSx = (theme) => ({
  color: theme.palette.text.secondary,
  marginLeft: '6px',
  maxWidth: '100%',
  paddingRight: 30,
});

const inlineActionSx = {
  fontWeight: 500,
  textTransform: 'none',
};

const progressSx = {
  marginBottom: 1,
  marginTop: 1,
};

const FileItem = ({
  file,
  removeFile,
  onReplaceFile,
  isUploading,
  fetchedUploadFile,
  setFile,
  hideDelete,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const url = file.url_path || URL.createObjectURL(file);

  const name = file.name || file.filename;
  const linkRef = useRef(null);

  const [toggleDeleteFile, DeleteFileConfirm] = useConfirm({
    title: i18n.t('documents.delete_file'),
    message: i18n.t('documents.delete_file_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: removeFile,
  });

  const downloadFile = useCallback(() => {
    if (linkRef.current) {
      linkRef.current.click();
    }
  }, []);

  useEffect(() => {
    if (!fetchedUploadFile && !isUploading && file) {
      setFile();
    }
  }, [fetchedUploadFile, isUploading, file, setFile]);

  return (
    <>
      <ul style={fileListSx}>
        {showPreview && (
          <Lightbox
            mainSrc={url}
            onCloseRequest={() => setShowPreview(false)}
            reactModalStyle={ligthBoxStyles}
          />
        )}
        <li style={fileItemSx} key={file.path || url}>
          <Box minWidth={20}>
            <BsPaperclip size={20} color="#4D4F5C" />
          </Box>
          <Box maxWidth="100%">
            <Typography sx={fileNameSx} variant="body2" noWrap component="span">
              {name}
              {isUploading && <LinearProgress sx={progressSx} />}
            </Typography>
            <ButtonGroup variant="text" color="primary" aria-label="file action" size="small">
              {url.match(imagePattern) ? (
                <Button sx={inlineActionSx} onClick={() => setShowPreview(true)}>
                  {i18n.t('documents.view_file')}
                </Button>
              ) : null}
              <Button color="primary" sx={inlineActionSx} onClick={downloadFile}>
                <a href={url} download={name} ref={linkRef} hidden>
                  {i18n.t('documents.download_file')}
                </a>
                {i18n.t('documents.download_file')}
              </Button>
              <Button sx={inlineActionSx} onClick={onReplaceFile}>
                {i18n.t('documents.replace_file')}
              </Button>
              {!hideDelete && (
                <Button sx={inlineActionSx} onClick={toggleDeleteFile}>
                  {i18n.t('documents.delete_file')}
                </Button>
              )}
            </ButtonGroup>
          </Box>
        </li>
      </ul>
      <DeleteFileConfirm />
    </>
  );
};

FileItem.propTypes = {
  file: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.shape({
      url_path: PropTypes.string,
      name: PropTypes.string,
      filename: PropTypes.string,
      path: PropTypes.string,
    }),
  ]).isRequired,
  removeFile: PropTypes.func.isRequired,
  onReplaceFile: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  fetchedUploadFile: PropTypes.bool,
  setFile: PropTypes.func,
  hideDelete: PropTypes.bool,
};

FileItem.defaultProps = {
  isUploading: false,
  fetchedUploadFile: false,
  setFile: () => {},
  hideDelete: false,
};

function VersionFileInput(props) {
  const [file, setFile] = useState(props.file);

  useEffect(() => {
    if (props.file) {
      setFile(props.file);
    }
  }, [props.file]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles.find((f) => f);
      const image = new window.Image();
      image.addEventListener('load', () => {
        if (props.size && (image.width > props.size?.width || image.height > props.size?.height)) {
          return props.onError();
        }
        if (props.size) {
          props.onChange(props.name, acceptedFiles[0]);
          setFile(acceptedFiles[0]);
        }
      });
      image.src = URL.createObjectURL(file);

      if (!props.size) {
        props.onChange(props.name, acceptedFiles[0]);
        setFile(acceptedFiles[0]);
      }
    },
    [props]
  );

  const removeFile = useCallback(() => {
    if (props.onRemoveFile) {
      props.onRemoveFile();
    }
    setFile();
  }, [props]);

  const {
    open: openFileManager,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    noClick: true,
  });

  const updateFile = () => {
    setFile(props.file);
  };

  // Compose dynamic border color based on drag state
  const getBorderColor = (theme) => {
    if (isDragReject) return theme.palette.error.main;
    if (isDragAccept) return theme.palette.success.main;
    if (isDragActive) return theme.palette.primary.main;
    return '#A8A8A8';
  };

  return (
    <>
      <input {...getInputProps()} />
      {file ? (
        <FileItem
          file={file}
          removeFile={removeFile}
          onReplaceFile={openFileManager}
          isUploading={props.isUploading}
          setFile={updateFile}
          hideDelete={props.hideDelete}
          fetchedUploadFile={props.fetchedUploadFile}
        />
      ) : (
        <Box
          sx={(theme) => ({
            ...fileInputBaseSx,
            borderColor: getBorderColor(theme),
          })}
          mt={2}
          width="100%"
          {...getRootProps()}
        >
          <MdCloudUpload size={34} color="rgba(0, 0, 0, 0.54)" />
          <Typography sx={fileInputCaptionSx} variant="body2">
            {i18n.t('documents.drop_file_instructions1')}
            <span
              role="button"
              tabIndex={0}
              onClick={openFileManager}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openFileManager();
                }
              }}
              style={fileInputCaptionActionSx}
            >
              {i18n.t('documents.drop_file_instructions2')}
            </span>
          </Typography>
        </Box>
      )}
    </>
  );
}

VersionFileInput.propTypes = {
  file: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.shape({
      url_path: PropTypes.string,
      name: PropTypes.string,
      filename: PropTypes.string,
      path: PropTypes.string,
    }),
  ]),
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func,
  isUploading: PropTypes.bool,
  hideDelete: PropTypes.bool,
  fetchedUploadFile: PropTypes.bool,
  size: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onError: PropTypes.func,
};

VersionFileInput.defaultProps = {
  file: undefined,
  name: '',
  onRemoveFile: undefined,
  isUploading: false,
  hideDelete: false,
  fetchedUploadFile: false,
  size: undefined,
  onError: () => {},
};

export default VersionFileInput;
